import isEqual from 'lodash/isEqual';
import round from 'lodash/round';
import capitalize from 'lodash/capitalize';

import {
  TooltipBulletStyle,
  TooltipItem,
  TooltipItemType,
  TooltipRow,
  TooltipSection,
} from 'common/components/chart-tooltip/types';
import {
  ChartType,
  EVENT_MATH_LABELS,
  MATH_TYPE,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  REPORT_LIMITS,
  ReportName,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {
  AbsoluteDateRange,
  FormulaClause,
  InsightsBookmarkParams,
  ReportSectionsMetadata,
  ShowClause,
} from 'common/report/insights/util/types';
import {
  computeDelta,
  formatDateRanges,
  isIncompleteFirstInterval,
  isIncompleteLastInterval,
  NUMERIC_DATA_PRECISION,
} from 'common/report/insights/util';
import {commaizeNumber, formatPercent, htmlEncodeString, isDefinedValue} from 'common/util';
import {pluralize, truncateMiddle} from 'common/util/string';
import {INSIGHTS_RESOURCE_TYPES} from 'common/widgets/property-filter-menu/models/insights-filter';
import {DISPLAY_DATE_FORMATS, formatDate, UNITS} from 'common/util/date';
import {reporter} from 'common/report/error-reporting';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {LiftType} from 'common/report/util/lift/types';

import {timestampToXauDateRange} from './time';

export enum ClauseType {
  Show = `show`,
  Formula = `formula`,
  Other = `other`,
}

export type BarChartTooltipStat = {
  count: number;
  dateRange: AbsoluteDateRange;
  percent: number;
  unit?: string;
  liftType?: LiftType;
};

type SharedClauseData = {
  metricKey: string;
};

export type FormulaClauseData = {
  clauseType: ClauseType.Formula;
} & FormulaClause &
  SharedClauseData;

export type ShowClauseData = {
  clauseType: ClauseType.Show;
  dataGroup?: DataGroup;
} & ShowClause &
  SharedClauseData;

export type OtherClauseData = {
  clauseType: ClauseType.Other;
  // The metric clauses corresponding to the unselected "Other" segments
  metricClauses: MetricClauseData[];
} & SharedClauseData;

export type MetricClauseData = ShowClauseData | FormulaClauseData | OtherClauseData;

export function computeMetricClauseByKey({
  sectionsMetadata,
  bookmarkParams,
  dataGroups,
}: {
  sectionsMetadata: ReportSectionsMetadata;
  bookmarkParams: InsightsBookmarkParams;
  dataGroups: DataGroup[];
}): Record<string, MetricClauseData> {
  const metricClauseByKey: Record<string, MetricClauseData> = {};
  if (sectionsMetadata.show) {
    sectionsMetadata.show.forEach((metadata, index) => {
      const clauseAttrs = bookmarkParams.sections.show[index];

      const dataGroup =
        clauseAttrs.dataGroupId && dataGroups
          ? dataGroups.find((dataGroup) => dataGroup.groupID === clauseAttrs.dataGroupId)
          : null;

      const metricClauseData: ShowClauseData = {
        ...clauseAttrs,
        ...(dataGroup ? {dataGroup} : {}),
        clauseType: ClauseType.Show,
        metricKey: metadata.metric_key,
      };

      if (metadata.sub_queries) {
        metadata.sub_queries
          .filter((subQuery) => subQuery.metric_key)
          .forEach((subQuery) => {
            metricClauseByKey[subQuery.metric_key] = {
              ...metricClauseData,
              metricKey: subQuery.metric_key,
            };
          });
      } else if (metadata.metric_key) {
        metricClauseByKey[metadata.metric_key] = metricClauseData;
      }
    });
  }

  if (sectionsMetadata.formula) {
    sectionsMetadata.formula.forEach((metadata, index) => {
      if (metadata.metric_key) {
        metricClauseByKey[metadata.metric_key] = {
          ...bookmarkParams.sections.formula[index],
          clauseType: ClauseType.Formula,
          metricKey: metadata.metric_key,
        };
      }
    });
  }

  return metricClauseByKey;
}

type FormattedMetricUnit =
  | string
  | [
      string, // singular
      string, // plural
    ];

function getMetricTooltipItems(count: number, metricUnit?: FormattedMetricUnit, shouldPluralize = true): TooltipItem[] {
  return [
    {text: commaizeNumber(count), type: TooltipItemType.Data},
    ...(metricUnit
      ? [
          {
            text: shouldPluralize ? getPluralizedMetricUnit(count, metricUnit) : getSingularMetricUnit(metricUnit),
            type: TooltipItemType.Text,
          },
        ]
      : []),
  ];
}

export function getSingularMetricUnit(metricUnit?: FormattedMetricUnit) {
  if (Array.isArray(metricUnit)) {
    return metricUnit.join(` `);
  }

  return metricUnit;
}

export function getPluralizedMetricUnit(count: number, metricUnit?: FormattedMetricUnit) {
  if (Array.isArray(metricUnit)) {
    return pluralize(metricUnit[0], count, metricUnit[1]);
  }

  return pluralize(metricUnit, count);
}

export type ComputeMetricNameRowsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
};

export function computeMetricNameRows({metricClauseData, segments}: ComputeMetricNameRowsParams): TooltipRow[] {
  const hasHistogramSegment =
    metricClauseData.clauseType === ClauseType.Show && metricClauseData.math === PRIMITIVE_MATH_TYPES.HISTOGRAM;
  const breakdownSegments = hasHistogramSegment ? segments.slice(0, -1) : segments;
  const histogramSegment = hasHistogramSegment ? segments[segments.length - 1] : null;
  let metricDisplayName;
  if (metricClauseData.clauseType === ClauseType.Other) {
    const unselectedSegmentCount = metricClauseData.metricClauses.length;

    // If we have potentially hit the max amounts of segments, don't name how many, just say we have a lot of segments
    if (
      unselectedSegmentCount + REPORT_LIMITS[ReportName.Insights].segments[ChartType.Line].max >=
      REPORT_LIMITS[ReportName.Insights].query
    ) {
      metricDisplayName = `All other segments`;
    } else {
      metricDisplayName = `Other (${unselectedSegmentCount} unselected ${pluralize(`value`, unselectedSegmentCount)})`;
    }
  } else {
    metricDisplayName = metricClauseData.metricKey;
  }
  if (histogramSegment) {
    metricDisplayName = `${metricDisplayName} (${histogramSegment})`;
  }

  const rows: TooltipRow[] = [];

  rows.push({
    items: [
      {
        text: metricDisplayName,
        type: TooltipItemType.Strong,
      },
    ],
  });
  if (breakdownSegments && breakdownSegments.length) {
    const segmentText = breakdownSegments
      .map((segment) => (segment === OTHER_SEGMENT_VALUE ? `Other (unselected values)` : segment))
      .join(` / `);
    rows.push({
      items: [
        {
          text: truncateMiddle(segmentText, 45),
          type: TooltipItemType.Text,
        },
      ],
    });
  }

  return rows;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function computeMetricUnit(metricClauseData: MetricClauseData): FormattedMetricUnit {
  if (metricClauseData.clauseType === ClauseType.Other) {
    if (metricClauseData.metricClauses.length === 0) {
      return null;
    }
    const metricUnit = computeMetricUnit(metricClauseData.metricClauses[0]);
    // Only show the unit if all the metrics in the "Other" segment are the same unit rather than mixed units
    return metricClauseData.metricClauses.slice(1).every((clause) => isEqual(computeMetricUnit(clause), metricUnit))
      ? metricUnit
      : null;
  } else if (metricClauseData.clauseType !== ClauseType.Show) {
    return null;
  }

  let metricUnit: FormattedMetricUnit = null;
  const dataGroupName = metricClauseData.dataGroupId ? metricClauseData.dataGroup?.displayName : `user`;

  if (metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_COHORTS) {
    metricUnit = dataGroupName;
  } else if (metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_EVENTS) {
    if (metricClauseData.perUserAggregation) {
      if (metricClauseData.math === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
        metricUnit = dataGroupName;
      } else if (metricClauseData.property) {
        metricUnit = dataGroupName ? [`per ${dataGroupName}`, `per ${dataGroupName}`] : null;
      } else if (metricClauseData.perUserAggregation === PER_USER_AGGREGATIONS.TOTAL) {
        metricUnit = dataGroupName ? [`event per ${dataGroupName}`, `events per ${dataGroupName}`] : null;
      }
    } else if (!metricClauseData.property) {
      metricUnit = {
        [XAU_MATH_TYPES.DAU]: dataGroupName ? `daily active ${dataGroupName}` : null,
        [XAU_MATH_TYPES.WAU]: dataGroupName ? `weekly active ${dataGroupName}` : null,
        [XAU_MATH_TYPES.MAU]: dataGroupName ? `monthly active ${dataGroupName}` : null,
        [PRIMITIVE_MATH_TYPES.SESSIONS]: `session`,
        [PRIMITIVE_MATH_TYPES.TOTAL]: `event`,
        [PRIMITIVE_MATH_TYPES.UNIQUE]: dataGroupName,
      }[metricClauseData.math];
    }
  } else if (
    metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_PEOPLE &&
    !metricClauseData.property &&
    metricClauseData.math === PRIMITIVE_MATH_TYPES.TOTAL
  ) {
    metricUnit = metricClauseData.dataGroupId ? dataGroupName : `profile`;
  }

  return metricUnit;
}

export type ComputeBarChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: BarChartTooltipStat;
  timeComparisonStat?: BarChartTooltipStat;
  liftComparisonStat?: BarChartTooltipStat;
  baselineComparisonStat?: BarChartTooltipStat;
  titleColor?: string;
};

export function computeBarChartTooltipSections(params: ComputeBarChartTooltipSectionsParams): TooltipSection[] {
  const {
    segments,
    metricClauseData,
    stat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    titleColor,
  } = params;

  const metricUnit = computeMetricUnit(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  const sections: TooltipSection[] = [{rows: metricNameRows}];

  if (timeComparisonStat && liftComparisonStat) {
    const [formattedDateRange, formattedTimeComparisonDateRange] = formatDateRanges([
      stat.dateRange,
      timeComparisonStat.dateRange,
    ]);

    const tooltipRows: TooltipRow[] = [
      {
        items: [
          {
            text: `${liftComparisonStat.percent > 0 ? `+` : ``}${round(
              liftComparisonStat.percent,
              NUMERIC_DATA_PRECISION,
            ).toString()}%`,
            type: TooltipItemType.Data,
            theme: liftComparisonStat.percent < 0 ? `negative` : `positive`,
          },
        ],
      },
      {
        items: getMetricTooltipItems(stat.count, formattedDateRange, false),
      },
      {
        items: getMetricTooltipItems(timeComparisonStat.count, formattedTimeComparisonDateRange, false),
      },
    ];

    sections.push({rows: tooltipRows});
  } else if (timeComparisonStat) {
    const [formattedDateRange, formattedTimeComparisonDateRange] = formatDateRanges([
      stat.dateRange,
      timeComparisonStat.dateRange,
    ]);

    const delta = timeComparisonStat.count === 0 ? 1 : computeDelta(stat.count, timeComparisonStat.count);
    const formattedDelta = formatPercent(delta);

    const tooltipRows = [
      {
        items: getMetricTooltipItems(stat.count, metricUnit),
      },
      {
        items: [
          {
            text: delta > 0 ? `+${formattedDelta}` : formattedDelta,
            theme: delta < 0 ? `negative` : `positive`,
            type: TooltipItemType.Data,
          },
          {
            text: `from previous period`,
            type: TooltipItemType.Text,
          },
        ],
      },
    ];
    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: formattedDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
        ].concat(tooltipRows),
      },
      {rows: []}, // for additional spacing to emphasize separation between sections
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: formattedTimeComparisonDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(timeComparisonStat.count, metricUnit),
          },
        ],
      },
    );
  } else if (liftComparisonStat && `math` in metricClauseData) {
    const tooltipRows = generateLiftOverallAndBaselineRows(
      stat,
      liftComparisonStat,
      baselineComparisonStat,
      metricUnit,
      metricClauseData.math,
    );

    sections.push({rows: tooltipRows});
  } else {
    sections.push({
      rows: [
        {
          items: getMetricTooltipItems(stat.count, metricUnit),
        },
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: `of overall`,
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    });
  }

  return sections;
}

function generateLiftOverallAndBaselineRows(stat, liftComparisonStat, baselineComparisonStat, metricUnit, math) {
  const isOverall = liftComparisonStat.liftType === LiftType.Overall;

  const liftValue = liftComparisonStat.percent;
  const liftFormattedText = `${liftValue >= 0 && !isOverall ? `+` : ``}${round(
    liftValue,
    NUMERIC_DATA_PRECISION,
  ).toString()}%`;

  let baselineComparisonName = baselineComparisonStat?.unit ?? metricUnit;
  let theme = liftValue < 0 ? `negative` : `positive`;

  if (isOverall) {
    // Since overall % is neither positive nor negative, we want a neutral theme (i.e. none)
    theme = ``;
    baselineComparisonName = `${capitalize(EVENT_MATH_LABELS[math])} ${baselineComparisonName}`;
  }

  return [
    {
      items: [
        {
          text: liftFormattedText,
          type: TooltipItemType.Data,
          theme,
        },
      ],
    },
    {
      items: getMetricTooltipItems(stat.count, liftComparisonStat?.unit ?? metricUnit, false),
    },
    {
      items: getMetricTooltipItems(baselineComparisonStat?.count || 0, baselineComparisonName ?? metricUnit, false),
    },
  ];
}

export type ComputeMetricChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  value: number;
};

export function computeMetricChartTooltipSections(params: ComputeMetricChartTooltipSectionsParams): TooltipSection[] {
  const {metricClauseData, segments, value} = params;

  return [
    {
      rows: computeMetricNameRows({metricClauseData, segments}),
    },
    {
      rows: [
        {
          items: getMetricTooltipItems(value, computeMetricUnit(metricClauseData)),
        },
      ],
    },
  ];
}

export function timestampToXauDateRangeFunction({unit, operator, isLastInterval = false, lastIntervalToDate = null}) {
  // These are the only units allowed in XAU queries, and the only ones this function handles
  const ALLOWED_UNITS = [UNITS[`day`], UNITS[`week`], UNITS[`month`]];
  if (!ALLOWED_UNITS.includes(unit)) {
    reporter.error(new Error(`Unit not in ${ALLOWED_UNITS}: "${unit}"`));
  }

  return function (timestamp) {
    const {fromDate, toDate} = timestampToXauDateRange(timestamp, {unit, operator, isLastInterval, lastIntervalToDate});
    return `${fromDate.format(DISPLAY_DATE_FORMATS[`week`])} - ${toDate.format(DISPLAY_DATE_FORMATS[`week`])}`;
  };
}

export function timestampToTimeUnitFunction({
  displayRangeIfWeek = true,
  unit,
}: {
  displayRangeIfWeek?: boolean;
  unit: string;
}) {
  return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek});
}

export type LineChartTooltipStat = {
  value: number;
  timestamp?: number;
  dateRange?: AbsoluteDateRange;
  segmentKey?: string;
};

export type ComputeLineChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  liftComparisonStat?: LineChartTooltipStat;
  baselineComparisonStat?: LineChartTooltipStat;
  overallComparisonStat?: LineChartTooltipStat;
  timeUnit: string;
  titleColor: string;
  // Used in stacked line chart mode
  percent?: number;
  isLastInterval: boolean;
};

export function computeLineChartTooltipSections(params: ComputeLineChartTooltipSectionsParams) {
  const {
    segments,
    metricClauseData,
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    percent,
    timeUnit,
    isLastInterval,
    titleColor,
  } = params;

  const formatDelta = (delta) => {
    if (!isDefinedValue(delta)) {
      return `N/A`;
    }
    const sign = delta > 0 ? `+` : ``;
    return `${sign}${formatPercent(delta)}`;
  };

  const theme = (delta): string => {
    if (!isDefinedValue(delta) || delta === 0 || Boolean(overallComparisonStat)) {
      return ``;
    }
    return delta > 0 ? `positive` : `negative`;
  };

  const metricUnit = computeMetricUnit(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  let timestampFormatter;
  // the operators that require a special date range calculation
  const xauDateRangeOperators: MATH_TYPE[] = [XAU_MATH_TYPES.MAU, XAU_MATH_TYPES.WAU];
  if (metricClauseData.clauseType === ClauseType.Show && xauDateRangeOperators.includes(metricClauseData.math)) {
    timestampFormatter = timestampToXauDateRangeFunction({
      operator: metricClauseData.math,
      unit: timeUnit,
      isLastInterval,
      lastIntervalToDate: stat.dateRange.toDate,
    });
  } else {
    timestampFormatter = (timestamp) => formatDate(timestamp, {unit: timeUnit});
  }

  const sections: TooltipSection[] = [];

  if (!timeComparisonStat) {
    sections.push({
      rows: [
        ...metricNameRows,
        {
          items: [
            {
              text: timestampFormatter(stat.timestamp),
              type: TooltipItemType.Detail,
            },
          ],
        },
      ],
    });
  } else {
    sections.push({rows: metricNameRows});
  }

  if (liftComparisonStat) {
    const liftTooltipRows = liftComparisonTooltips(
      stat,
      liftComparisonStat,
      theme,
      timestampFormatter,
      baselineComparisonStat,
      overallComparisonStat,
      timeComparisonStat,
    );

    sections.push({rows: liftTooltipRows});
  } else if (timeComparisonStat) {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(round(stat.value, NUMERIC_DATA_PRECISION), metricUnit),
      },
    ];

    if (Number.isFinite(timeComparisonStat.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, timeComparisonStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: `from previous period`,
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(stat.timestamp),
                type: TooltipItemType.Detail,
              },
            ],
          },
        ].concat(tooltipRows),
      },
      {rows: []}, // for additional spacing for extra emphasis in this case
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(timeComparisonStat.timestamp),
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(round(timeComparisonStat.value, NUMERIC_DATA_PRECISION), metricUnit),
          },
        ],
      },
    );
  } else {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(round(stat.value, NUMERIC_DATA_PRECISION), metricUnit),
      },
    ];

    if (Number.isFinite(percent)) {
      tooltipRows.push({
        items: [
          {
            text: formatPercent(percent / 100),
            type: TooltipItemType.Data,
          },
          {
            text: `of total`,
            type: TooltipItemType.Text,
          },
        ],
      });
    } else if (Number.isFinite(deltaStat?.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, deltaStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: `from previous ${timeUnit}`,
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push({rows: tooltipRows});
  }

  return sections;
}

function liftComparisonTooltips(
  stat,
  liftComparisonStat,
  theme,
  timestampFormatter,
  baselineComparisonStat,
  overallComparisonStat,
  timeComparisonStat,
): TooltipRow[] {
  const liftValue = liftComparisonStat.value;
  const liftFormattedText = `${liftValue >= 0 && !overallComparisonStat ? `+` : ``}${round(
    liftValue * 100,
    NUMERIC_DATA_PRECISION,
  ).toString()}%`;
  const tooltipRows: TooltipRow[] = [
    {
      items: [
        {
          text: liftFormattedText,
          type: TooltipItemType.Data,
          theme: theme(liftValue),
        },
      ],
    },
    {
      items: getMetricTooltipItems(
        round(stat.value, NUMERIC_DATA_PRECISION),
        timeComparisonStat ? timestampFormatter(stat.timestamp) : liftComparisonStat.segmentKey,
        false,
      ),
    },
  ];

  if (timeComparisonStat) {
    tooltipRows.push({
      items: getMetricTooltipItems(
        round(timeComparisonStat?.value, NUMERIC_DATA_PRECISION),
        timestampFormatter(timeComparisonStat.timestamp),
        false,
      ),
    });
  }

  if (overallComparisonStat) {
    tooltipRows.push({
      items: getMetricTooltipItems(
        round(overallComparisonStat?.value, NUMERIC_DATA_PRECISION),
        overallComparisonStat.segmentKey,
        false,
      ),
    });
  } else if (baselineComparisonStat) {
    const lastSegment: string = baselineComparisonStat.segmentKey.split(`,`).pop().toString();
    tooltipRows.push({
      items: getMetricTooltipItems(round(baselineComparisonStat?.value, NUMERIC_DATA_PRECISION), lastSegment, false),
    });
  }

  return tooltipRows;
}

export type RenderLineChartTooltipParams = {
  headers: string[];
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  liftComparisonStat?: LineChartTooltipStat;
  baselineComparisonStat?: LineChartTooltipStat;
  overallComparisonStat?: LineChartTooltipStat;
  timeUnit: string;
  titleColor: string;
  percent?: number;
  isFirstInterval: boolean;
  isLastInterval: boolean;
  utcOffset: number;
  isComparisonItem: boolean;
};

export function renderLineChartTooltip({
  metricClauseData,
  segments,
  stat,
  deltaStat,
  timeComparisonStat,
  liftComparisonStat,
  baselineComparisonStat,
  overallComparisonStat,
  timeUnit,
  titleColor,
  percent,
  isFirstInterval,
  isLastInterval,
  utcOffset,
}: RenderLineChartTooltipParams) {
  const mathOperator = metricClauseData.clauseType === ClauseType.Show ? metricClauseData.math : null;

  const lastIntervalToDate = stat.dateRange.toDate;
  let isIncomplete = false;
  if (isFirstInterval) {
    isIncomplete = isIncompleteFirstInterval(stat.timestamp, stat.dateRange.fromDate, {
      mathOperator,
      utcOffset,
    });
  } else if (isLastInterval) {
    isIncomplete = isIncompleteLastInterval(stat.timestamp, lastIntervalToDate, {
      mathOperator,
      unit: timeUnit,
      utcOffset,
    });
  }

  if (timeComparisonStat) {
    if (isFirstInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteFirstInterval(timeComparisonStat.timestamp, timeComparisonStat.dateRange.fromDate, {
          mathOperator,
          utcOffset,
        });
    } else if (isLastInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteLastInterval(timeComparisonStat.timestamp, timeComparisonStat.dateRange.toDate, {
          mathOperator,
          unit: timeUnit,
          utcOffset,
        });
    }
  }

  const footerTextAttr = isIncomplete ? `footer-text="Incomplete ${timeUnit}"` : ``;
  const titleColorAttr = !timeComparisonStat ? `title-color="${titleColor}"` : ``;

  const sections = computeLineChartTooltipSections({
    metricClauseData,
    segments,
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    timeUnit,
    titleColor,
    percent,
    isLastInterval,
  });

  return `<mp-chart-tooltip ${titleColorAttr} sections='${htmlEncodeString(
    JSON.stringify(sections),
  )}'  ${footerTextAttr}>`;
}

export type PieChartTooltipStat = {
  count: number;
  percent: number;
};

export type ComputePieChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: PieChartTooltipStat;
};

export function computePieChartTooltipSections(params: ComputePieChartTooltipSectionsParams) {
  const {metricClauseData, segments, stat} = params;

  return [
    {
      rows: computeMetricNameRows({metricClauseData, segments}),
    },
    {
      rows: [
        {
          items: getMetricTooltipItems(stat.count, computeMetricUnit(metricClauseData)),
        },
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: `of total`,
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    },
  ];
}

type RenderPieChartTooltipParams = {
  value: number;
  percent: number;
  color: string;
  metricKey?: string;
  segments?: string[];
  otherMetricKeys?: string[];
  metricClauseByKey: Record<string, MetricClauseData>;
};

export function renderPieChartTooltip({
  value,
  percent,
  color,
  metricKey,
  segments,
  otherMetricKeys,
  metricClauseByKey,
}: RenderPieChartTooltipParams): string {
  let metricClauseData: MetricClauseData;
  if (otherMetricKeys) {
    metricClauseData = {
      clauseType: ClauseType.Other,
      metricClauses: otherMetricKeys.map((metricKey) => metricClauseByKey[metricKey]),
      metricKey: OTHER_SEGMENT_VALUE,
    };
    segments = [];
  } else {
    metricClauseData = metricClauseByKey[metricKey];
  }

  const sections = computePieChartTooltipSections({
    metricClauseData,
    segments,
    stat: {
      count: value,
      percent,
    },
  });

  return `<mp-chart-tooltip title-color="${color}" sections='${htmlEncodeString(JSON.stringify(sections))}'>`;
}
